<script setup>
  import { storeToRefs } from "pinia";
  import { computed, onMounted } from "vue";
  import { useFieldArray } from "vee-validate";
  import { Plus, Trash } from "lucide-vue-next";
  import { watchDebounced } from "@vueuse/core";
  import { computedIntegerListOptions } from "@utils/computeAnswerOptions";
  import { useEmailCampaignStore } from "@stores/survey/mailing/emailCampaign";
  import { isEmpty } from "@utils/toolBox";

  import { Input } from "@/components/ui/input";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";

  const emailCampaignStore = useEmailCampaignStore();
  const { isCampaignEditable, campaignListAttributes, getListAttributeKeyOptions } = storeToRefs(emailCampaignStore);
  const { getListAttributeValueOptions, fetchListAttributes, estimateEmailCampaignAudience } = emailCampaignStore;

  const { fields, push, remove, update } = useFieldArray("conditions");

  const bucketOptions = computedIntegerListOptions(1, 100);
  const conditionTypeOptions = [
    { label: "Respondent status", value: "respondent_status" },
    { label: "Custom attribute", value: "custom_attribute" },
    { label: "Bucket", value: "bucket" },
  ];
  const relationOptions = [
    { label: ">=", value: "greater_than" },
    { label: "<=", value: "less_than" },
  ];
  const statusOptions = [
    { label: "Not started", value: "not_started" },
    { label: "Started", value: "started" },
    { label: "Completed", value: "completed" },
  ];

  const hasMultipleConditions = computed(() => fields.value.length > 1);
  const isOpenEndedAttribute = (name) => campaignListAttributes.value[name] === "open_ended_attribute";

  const onTypeChange = (idx, type) => update(idx, { type, value: [] });
  const onNameChange = (idx, name) => update(idx, { ...fields.value[idx].value, name, value: [] });

  onMounted(() => fetchListAttributes());
  watchDebounced(
    () => fields.value.map((f) => [f.value.value, f.value.relation]),
    async () => {
      if (!fields.value.some((f) => isEmpty(f.value.value))) await estimateEmailCampaignAudience();
    },
    { debounce: 1000 },
  );
</script>

<template>
  <div class="space-y-2">
    <div
      v-for="(field, index) in fields"
      :key="field.key"
      class="flex items-center gap-2">
      <span
        v-if="hasMultipleConditions"
        class="w-10 text-center text-xs font-medium text-muted-foreground shrink-0">
        <template v-if="index !== 0">AND</template>
      </span>

      <div class="flex-1 flex items-center gap-2">
        <Select
          :model-value="field.value.type"
          :options="conditionTypeOptions"
          :disabled="!isCampaignEditable"
          class="w-44 shrink-0"
          @update:model-value="(v) => onTypeChange(index, v)" />

        <div
          v-if="field.value.type === 'respondent_status'"
          class="flex-1">
          <Select
            v-model="field.value.value"
            :options="statusOptions"
            multiple
            :disabled="!isCampaignEditable" />
        </div>

        <template v-else-if="field.value.type === 'custom_attribute'">
          <div class="flex-1 flex gap-1">
            <Select
              :model-value="field.value.name"
              :options="getListAttributeKeyOptions"
              filterable
              :disabled="!isCampaignEditable"
              class="flex-1"
              @update:model-value="(v) => onNameChange(index, v)" />
            <Input
              v-if="isOpenEndedAttribute(field.value.name)"
              v-model="field.value.value"
              :disabled="!isCampaignEditable"
              class="flex-1" />
            <Select
              v-else
              v-model="field.value.value"
              :options="getListAttributeValueOptions(field.value.name)"
              filterable
              multiple
              :disabled="!isCampaignEditable"
              class="flex-1" />
          </div>
        </template>

        <template v-else-if="field.value.type === 'bucket'">
          <div class="flex-1 flex gap-1">
            <Select
              v-model="field.value.relation"
              :options="relationOptions"
              :disabled="!isCampaignEditable"
              class="flex-1" />
            <Select
              v-model="field.value.value"
              :options="bucketOptions"
              filterable
              :disabled="!isCampaignEditable"
              class="flex-1" />
          </div>
        </template>
      </div>

      <Button
        variant="ghost"
        size="icon"
        :disabled="!isCampaignEditable"
        class="shrink-0"
        @click="remove(index)">
        <Trash
          :stroke-width="1.5"
          class="size-3.5" />
      </Button>
    </div>

    <Button
      variant="outline"
      :disabled="!isCampaignEditable"
      @click="push({ type: 'respondent_status', value: [] })">
      <Plus
        :stroke-width="1.5"
        class="size-3.5 mr-1" />
      Add condition
    </Button>
  </div>
</template>
